<template>
  <div class="cloud-storage-container pa-4">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#005f32"
      spinner="bar-fade-scale"
    />
    
    <v-snackbar v-model="showsnackbar" :color="snackbarColor" right top elevation="4">
      <div class="d-flex align-center">
        <v-icon left dark v-if="snackbarColor === 'success'">mdi-check-circle</v-icon>
        <v-icon left dark v-else-if="snackbarColor === 'error'">mdi-alert-circle</v-icon>
        <span>{{ msg }}</span>
        <v-spacer></v-spacer>
        <v-btn small icon dark @click="showsnackbar = false" class="ml-2">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <v-card class="elevation-2 rounded-lg overflow-hidden border-light">
      <!-- Top Header / Toolbar -->
      <div class="px-6 py-4 white d-flex align-center justify-space-between flex-wrap border-bottom">
        <div class="d-flex align-center py-1">
          <v-btn icon @click="$router.go(-1)" class="mr-3 grey lighten-4" small>
            <v-icon color="#2c3e50">mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <h2 class="text-h6 font-weight-bold mb-0" style="color: #1a252c; line-height: 1.2;">
              {{ employeeName ? employeeName + "'s Documents" : "Employee Folder" }}
            </h2>
            <span class="caption text--secondary">
              Total Documents: <strong style="color: #005f32">{{ documentList.length }}</strong>
            </span>
          </div>
        </div>

        <div class="d-flex align-center flex-wrap gap-2 mt-2 mt-sm-0">
          <!-- Search Bar -->
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            placeholder="Search documents..."
            dense
            outlined
            hide-details
            clearable
            color="#005f32"
            class="search-input mr-3"
            style="max-width: 240px;"
          ></v-text-field>

          <v-btn
            color="#005f32"
            dark
            class="rounded-md font-weight-bold text-capitalize px-4 shadow-sm"
            depressed
            height="40"
            @click="uploadDialog = true"
          >
            <v-icon left size="20">mdi-cloud-upload</v-icon>
            Upload Document
          </v-btn>
        </div>
      </div>

      <!-- Main Content Area -->
      <v-card-text class="grey lighten-5 pa-6" style="min-height: 420px;">
        <!-- Empty State -->
        <div v-if="documentList.length === 0 && !appLoading" class="text-center py-12">
          <div class="empty-icon-wrapper mb-4">
            <v-icon size="64" color="#005f32">mdi-folder-open-outline</v-icon>
          </div>
          <h3 class="font-weight-bold text-h6 grey--text text--darken-2 mb-1">No documents uploaded yet</h3>
          <p class="grey--text text--darken-1 mb-5 body-2" style="max-width: 400px; margin: 0 auto;">
            Upload employee documents like ID proofs, certificates, or resumes to store them securely.
          </p>
          <v-btn
            color="#005f32"
            dark
            outlined
            class="font-weight-bold text-capitalize rounded-md"
            @click="uploadDialog = true"
          >
            <v-icon left>mdi-plus</v-icon> Add First Document
          </v-btn>
        </div>

        <!-- Document Table -->
        <div v-else>
          <v-data-table
            :headers="headers"
            :items="documentList"
            :search="search"
            class="elevation-0 transparent-table custom-doc-table"
            :items-per-page="10"
            hide-default-footer
            :page.sync="currentPage"
            @page-count="pages = $event"
          >
            <template v-slot:item.docName="{ item }">
              <div class="d-flex align-center py-3">
                <div class="doc-icon-avatar mr-3" :class="getFileBgClass(item.docUrl)">
                  <v-icon :color="getFileIconColor(item.docUrl)" size="22">{{ getFileIcon(item.docUrl) }}</v-icon>
                </div>
                <div>
                  <div class="font-weight-bold text-subtitle-2" style="color: #2c3e50;">
                    {{ item.docName }}
                  </div>
                  <div class="caption text--secondary" v-if="getFileExtension(item.docUrl)">
                    <v-chip x-small color="grey lighten-3" class="text-uppercase font-weight-bold grey--text text--darken-2">
                      {{ getFileExtension(item.docUrl) }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </template>
            
            <template v-slot:item.actions="{ item }">
              <div class="d-flex align-center justify-end">
                <v-btn
                  icon
                  small
                  color="teal"
                  class="teal lighten-5 mr-2"
                  @click="viewDocument(item.docUrl)"
                >
                  <v-icon size="18">mdi-eye-outline</v-icon>
                </v-btn>

                <v-btn
                  icon
                  small
                  color="error"
                  class="red lighten-5"
                  @click="confirmDelete(item)"
                >
                  <v-icon size="18">mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
          
          <div class="d-flex justify-center pt-6" v-if="pages > 1">
            <v-pagination
              prev-icon="mdi-chevron-left"
              next-icon="mdi-chevron-right"
              :length="pages"
              :total-visible="7"
              v-model="currentPage"
              color="#005f32"
            ></v-pagination>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Enhanced Upload Dialog -->
    <v-dialog v-model="uploadDialog" max-width="520px" persistent transition="dialog-center-transition">
      <v-card class="rounded-xl overflow-hidden">
        <v-card-title class="px-6 py-4 white border-bottom d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-avatar color="#e6f0ec" size="36" class="mr-3">
              <v-icon color="#005f32">mdi-cloud-upload</v-icon>
            </v-avatar>
            <span class="font-weight-bold text-h6" style="color: #2c3e50;">Upload Documents</span>
          </div>
          <v-btn icon small @click="closeUploadDialog">
            <v-icon color="grey darken-1">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-6">
          <!-- Drag and Drop Box -->
          <div
            class="upload-box"
            :class="{ 'is-dragging': isDragging }"
            @click="$refs.fileInput.click()"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
          >
            <div class="upload-icon-circle mb-3">
              <v-icon size="36" color="#005f32">mdi-cloud-upload-outline</v-icon>
            </div>
            <div class="font-weight-bold text-body-1 mb-1" style="color: #2c3e50;">
              Click or drag & drop files here
            </div>
            <div class="caption text--secondary">
              Supports multiple files (PDF, DOCX, XLSX, PNG, JPG)
            </div>
          </div>

          <input
            type="file"
            ref="fileInput"
            style="display: none"
            multiple
            @change="handleFileUpload"
          />

          <!-- Selected Files Section -->
          <div v-if="files.length > 0" class="mt-5">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="caption font-weight-bold grey--text text--darken-2">
                SELECTED FILES ({{ files.length }})
              </span>
              <v-btn text x-small color="error" class="px-0 font-weight-bold" @click="files = []">
                Clear All
              </v-btn>
            </div>

            <div class="selected-files-list">
              <div
                v-for="(file, index) in files"
                :key="index"
                class="file-item-row d-flex align-center justify-space-between pa-2 mb-2 rounded-lg grey lighten-4"
              >
                <div class="d-flex align-center overflow-hidden mr-2">
                  <v-icon small color="#005f32" class="mr-2">mdi-file-document-outline</v-icon>
                  <div class="text-truncate caption font-weight-bold" style="color: #2c3e50;">
                    {{ file.name }}
                  </div>
                  <span class="caption text--secondary ml-2 font-weight-regular" v-if="file.size">
                    ({{ formatBytes(file.size) }})
                  </span>
                </div>
                <v-btn icon x-small color="grey darken-1" @click.stop="removeFile(index)">
                  <v-icon size="16">mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="px-6 pb-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text class="font-weight-bold rounded-md" @click="closeUploadDialog" :disabled="isUploading">
            CANCEL
          </v-btn>
          <v-btn
            color="#005f32"
            dark
            depressed
            class="font-weight-bold rounded-md px-6 text-capitalize"
            @click="uploadDocument"
            :loading="isUploading"
            :disabled="files.length === 0 || isUploading"
          >
            Upload {{ files.length > 0 ? `(${files.length})` : '' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="420px" persistent transition="dialog-center-transition">
      <v-card class="rounded-xl overflow-hidden">
        <div class="pa-6 text-center">
          <v-avatar color="red lighten-5" size="64" class="mb-3">
            <v-icon color="error" size="32">mdi-alert-outline</v-icon>
          </v-avatar>
          <h3 class="font-weight-bold text-h6 mb-2" style="color: #1a252c;">Delete Document</h3>
          <p class="grey--text text--darken-1 body-2 mb-0">
            Are you sure you want to delete <strong style="color: #2c3e50;">{{ docToDelete ? docToDelete.docName : '' }}</strong>? This action cannot be undone.
          </p>
        </div>
        <v-card-actions class="px-6 pb-6 pt-0 justify-center">
          <v-btn color="grey darken-1" text class="font-weight-bold rounded-md px-4" @click="deleteDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" depressed class="font-weight-bold rounded-md px-6 text-capitalize" @click="deleteDocument">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      employeeId: null,
      employeeName: "",
      documentList: [],
      currentPage: 1,
      pages: 0,
      search: "",
      headers: [
        { text: 'Document Name', value: 'docName', align: 'start' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'end' }
      ],
      
      showsnackbar: false,
      msg: "",
      snackbarColor: "black",
      
      uploadDialog: false,
      isDragging: false,
      isUploading: false,
      files: [],
      
      deleteDialog: false,
      docToDelete: null
    };
  },
  mounted() {
    this.employeeId = this.$route.query.id;
    if (this.employeeId) {
      this.fetchEmployeeDetails();
      this.fetchDocuments();
    } else {
      this.showMessage("Employee ID not found", "error");
    }
  },
  methods: {
    showMessage(msg, color = "black") {
      this.msg = msg;
      this.snackbarColor = color;
      this.showsnackbar = true;
    },
    
    getFileIcon(filename) {
      if (!filename) return "mdi-file-document-outline";
      const ext = this.getFileExtension(filename);
      if (['pdf'].includes(ext)) return "mdi-file-pdf-box";
      if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return "mdi-file-image-outline";
      if (['doc', 'docx'].includes(ext)) return "mdi-file-word-box";
      if (['xls', 'xlsx', 'csv'].includes(ext)) return "mdi-file-excel-box";
      return "mdi-file-document-outline";
    },
    
    getFileIconColor(filename) {
      if (!filename) return "grey darken-1";
      const ext = this.getFileExtension(filename);
      if (['pdf'].includes(ext)) return "#d32f2f";
      if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return "#0288d1";
      if (['doc', 'docx'].includes(ext)) return "#1565c0";
      if (['xls', 'xlsx', 'csv'].includes(ext)) return "#2e7d32";
      return "#546e7a";
    },

    getFileBgClass(filename) {
      if (!filename) return "grey lighten-4";
      const ext = this.getFileExtension(filename);
      if (['pdf'].includes(ext)) return "red lighten-5";
      if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return "light-blue lighten-5";
      if (['doc', 'docx'].includes(ext)) return "blue lighten-5";
      if (['xls', 'xlsx', 'csv'].includes(ext)) return "green lighten-5";
      return "grey lighten-4";
    },

    getFileExtension(filename) {
      if (!filename) return "";
      return filename.split('.').pop().toLowerCase();
    },

    formatBytes(bytes, decimals = 1) {
      if (!bytes || bytes === 0) return '0 B';
      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    },

    handleDrop(e) {
      this.isDragging = false;
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const droppedFiles = Array.from(e.dataTransfer.files);
        this.files = [...this.files, ...droppedFiles];
      }
    },
    
    viewDocument(filename) {
      if (filename) {
        window.open('https://wtiadmin.in/i/' + filename, '_blank');
      }
    },
    
    fetchEmployeeDetails() {
      axios({
        url: "/employee/details/view",
        method: "GET",
        params: { id: this.employeeId },
        headers: { "x-auth-token": localStorage.getItem("token") }
      }).then(response => {
        if (response.data && response.data.data) {
          const emp = response.data.data;
          this.employeeName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
        }
      }).catch(err => console.log(err));
    },
    
    fetchDocuments() {
      this.appLoading = true;
      axios({
        url: `/employeedocs/view/${this.employeeId}`,
        method: "GET",
        headers: { "x-auth-token": localStorage.getItem("token") }
      })
      .then(response => {
        this.appLoading = false;
        if (response.data.status) {
          this.documentList = response.data.doc || [];
        } else {
          this.documentList = [];
        }
      })
      .catch(err => {
        this.appLoading = false;
        console.log(err);
        this.documentList = [];
      });
    },
    
    handleFileUpload(e) {
      if (e.target.files && e.target.files.length > 0) {
        const selectedFiles = Array.from(e.target.files);
        this.files = [...this.files, ...selectedFiles];
      }
    },
    
    removeFile(index) {
      this.files.splice(index, 1);
    },
    
    closeUploadDialog() {
      this.uploadDialog = false;
      this.isDragging = false;
      this.isUploading = false;
      this.files = [];
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    
    async uploadDocument() {
      if (this.files.length === 0 || this.isUploading) return;
      
      const formData = new FormData();
      this.files.forEach(file => {
        formData.append('documentUrl', file);
      });
      
      this.isUploading = true;
      try {
        const res = await axios({
          url: `/employeedocs/upload/${this.employeeId}`,
          method: 'POST',
          headers: {
            "x-auth-token": localStorage.getItem("token"),
            "Content-Type": "multipart/form-data"
          },
          data: formData
        });
        
        this.isUploading = false;
        this.closeUploadDialog();
        this.showMessage(res.data.message || res.data.msg || "Document(s) uploaded successfully", "success");
        this.fetchDocuments();
      } catch (err) {
        this.isUploading = false;
        this.showMessage("Failed to upload document(s)", "error");
        console.log(err);
      }
    },
    
    confirmDelete(doc) {
      this.docToDelete = doc;
      this.deleteDialog = true;
    },
    
    deleteDocument() {
      if (!this.docToDelete || !this.docToDelete._id) return;
      
      this.appLoading = true;
      axios({
        url: `/employeedocs/delete/${this.docToDelete._id}`,
        method: "DELETE",
        headers: { "x-auth-token": localStorage.getItem("token") }
      })
      .then(response => {
        this.appLoading = false;
        this.deleteDialog = false;
        this.docToDelete = null;
        this.showMessage(response.data.msg || "Document deleted", "success");
        this.fetchDocuments();
      })
      .catch(err => {
        this.appLoading = false;
        this.deleteDialog = false;
        this.showMessage("Failed to delete document", "error");
        console.log(err);
      });
    }
  }
};
</script>

<style scoped>
.cloud-storage-container {
  background-color: transparent;
}

.border-light {
  border: 1px solid #e2e8f0 !important;
}

.border-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}

.doc-icon-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.empty-icon-wrapper {
  width: 100px;
  height: 100px;
  background-color: #e6f0ec;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.upload-box {
  border: 2px dashed #005f32;
  border-radius: 16px;
  padding: 36px 20px;
  text-align: center;
  cursor: pointer;
  background-color: #f2f7f4;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.upload-box:hover,
.upload-box.is-dragging {
  background-color: #e6f0ec;
  border-color: #005f32;
  box-shadow: 0 4px 12px rgba(0, 95, 50, 0.12);
  transform: translateY(-2px);
}

.upload-icon-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.selected-files-list {
  max-height: 180px;
  overflow-y: auto;
}

.file-item-row {
  border: 1px solid #e2e8f0;
  transition: background-color 0.15s ease;
}

.file-item-row:hover {
  background-color: #e6f0ec !important;
}

.shadow-sm {
  box-shadow: 0 2px 4px rgba(0, 95, 50, 0.2) !important;
}

.custom-doc-table >>> tbody tr {
  transition: background-color 0.15s ease;
}

.custom-doc-table >>> tbody tr:hover {
  background-color: #f8fafc !important;
}
</style>
